import type { DataQueryListQuery } from '@/features/data-query/api';
import type { DataQueryExecution, DataQueryResponse } from '@/features/data-query/types';

const DAY_MS = 86_400_000;
const KST_OFFSET_MS = 9 * 3_600_000;

export const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  DELIVERED: '전달됨', BUSY: '대기 초과', CLARIFIED: '조건 확인',
  FAILED_SQL: '실패 · SQL', FAILED_CONDITION: '실패 · 조건 변경 필요',
  FAILED_TIMEOUT: '실패 · 시간 초과', FAILED_ROW_LIMIT: '실패 · 행 수 초과',
  FAILED_SIZE_LIMIT: '실패 · 용량 초과', FAILED_CONNECTION: '실패 · DB 연결',
  FAILED_LOCK_LOST: '실패 · 잠금 유실', FAILED_ANSWER: '실패 · 답변 생성',
  FAILED_RECORD: '실패 · 기록', DELIVERY_FAILED: '전달 실패', DELIVERY_UNKNOWN: '전달 모름',
};

export interface DataQueryFilters {
  readonly from: string;
  readonly to: string;
  readonly requester: string;
  readonly outcome: string;
}

export class DataQueryFormat {
  static label(outcome: string): string { return OUTCOME_LABELS[outcome] ?? outcome; }
  static number(value: number | null): string { return value === null ? '—' : value.toLocaleString('ko-KR'); }
  static kst(value: string): string { return new Date(value).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }); }
  static pages(data: DataQueryResponse | null): number { return data ? Math.max(1, Math.ceil(data.total / data.size)) : 1; }
  static requester(execution: Pick<DataQueryExecution, 'userId' | 'requesterName'>): string {
    return execution.requesterName || execution.userId;
  }
  static preview(question: string): string { return question.length > 80 ? `${question.slice(0, 80)}…` : question; }

  static initial(now = new Date()): DataQueryFilters {
    const kst = now.getTime() + KST_OFFSET_MS;
    return { from: new Date(kst - 6 * DAY_MS).toISOString().slice(0, 10), to: new Date(kst).toISOString().slice(0, 10), requester: '', outcome: '' };
  }

  static query(filters: DataQueryFilters, page = 1, size = 50): DataQueryListQuery {
    const from = new Date(`${filters.from}T00:00:00+09:00`);
    const to = new Date(`${filters.to}T23:59:59.999+09:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) throw new Error('시작일과 종료일을 입력해 주세요.');
    if (from > to) throw new Error('시작일은 종료일보다 늦을 수 없습니다.');
    if (to.getTime() - from.getTime() > 31 * DAY_MS) throw new Error('조회 기간은 최대 31일입니다.');
    return { from: from.toISOString(), to: to.toISOString(), requester: filters.requester.trim() || undefined, outcome: filters.outcome || undefined, page, size };
  }

  static threadUrl(value: string | null): string | null {
    if (!value) return null;
    try { const url = new URL(value); return url.protocol === 'https:' ? url.href : null; } catch { return null; }
  }
}
